import { useEffect, useRef } from 'react';

/**
 * Default filter shape for model pages when deep-linking to a player. Spreads onto existing
 * state so unknown keys are preserved if a page adds more toggles later.
 */
const UNOBSTRUCTED_FILTERS = {
  top5: false,
  inLineup: false,
  featuredPicks: false,
  hasntStarted: false,
  hasntEnded: false,
};

/** Stop retrying row match after this many effect runs with a non-empty list but no match. */
const MAX_EXPAND_MISS_ATTEMPTS = 8;

/**
 * True when URL requests an expanded card/row for a specific player.
 *
 * @param {URLSearchParams} searchParams
 * @returns {boolean}
 */
function wantsPlayerExpanded(searchParams) {
  const raw = searchParams.get('player');
  return raw != null && String(raw).trim() !== '';
}

/**
 * Apply ``?player=...`` and ``?expand=1`` from the URL: seed search, clear filters that would
 * hide the player (lineup, featured, top 5, game-status toggles), clear team filter, then expand
 * the matching card/table row once data is ready.
 *
 * A non-empty ``player`` query always opens that row expanded (``expand=1`` is optional).
 *
 * Re-applies unobstructed filters after predictions load so effects that auto-enable "top 5"
 * (when there are no featured picks) cannot keep the linked player off-screen.
 *
 * @param {object} args
 * @param {import('react-router-dom').URLSearchParams} args.searchParams
 * @param {import('react').Dispatch<import('react').SetStateAction<string>>} args.setPlayerSearch
 * @param {import('react').Dispatch<import('react').SetStateAction<Record<string, unknown>>} args.setFilters
 * @param {import('react').Dispatch<import('react').SetStateAction<number[]>>} args.setExpandedRows
 * @param {boolean} args.loading
 * @param {unknown[]} args.displayedPredictions
 * @param {import('react').Dispatch<import('react').SetStateAction<string>>} [args.setTeamFilter] - Clears team dropdown when deep-linking
 */
export function useDeepLinkPlayerExpand({
  searchParams,
  setPlayerSearch,
  setFilters,
  setExpandedRows,
  loading,
  displayedPredictions,
  setTeamFilter,
}) {
  const expandAppliedRef = useRef(false);
  const deepLinkKeyRef = useRef('');
  const expandMissCountRef = useRef(0);

  const hasPlayerParam = () => {
    const raw = searchParams.get('player');
    return raw != null && String(raw).trim() !== '';
  };

  useEffect(() => {
    const player = searchParams.get('player');
    if (player != null && player.trim() !== '') {
      setPlayerSearch(decodeURIComponent(player.trim()));
      setFilters((prev) => ({ ...prev, ...UNOBSTRUCTED_FILTERS }));
      if (typeof setTeamFilter === 'function') {
        setTeamFilter('');
      }
    } else if (searchParams.get('expand') === '1') {
      setFilters((prev) => ({ ...prev, top5: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync URL to state once on mount
  }, []);

  useEffect(() => {
    if (!hasPlayerParam()) {
      if (searchParams.get('expand') === '1' && !loading) {
        setFilters((prev) => (prev.top5 ? { ...prev, top5: false } : prev));
      }
      return;
    }
    if (loading) return;
    setFilters((prev) => ({ ...prev, ...UNOBSTRUCTED_FILTERS }));
  }, [loading, searchParams, setFilters]);

  useEffect(() => {
    const raw = searchParams.get('player');
    const linkKey = `${searchParams.get('date') ?? ''}|${raw ?? ''}|${searchParams.get('expand') ?? ''}`;
    if (linkKey !== deepLinkKeyRef.current) {
      deepLinkKeyRef.current = linkKey;
      expandAppliedRef.current = false;
      expandMissCountRef.current = 0;
    }

    const shouldExpandRow = wantsPlayerExpanded(searchParams) || searchParams.get('expand') === '1';
    if (!shouldExpandRow) return;
    if (loading) return;

    if (raw == null || String(raw).trim() === '') {
      expandAppliedRef.current = true;
      return;
    }

    if (!displayedPredictions || displayedPredictions.length === 0) return;

    if (expandAppliedRef.current) return;

    const needle = decodeURIComponent(String(raw).trim()).toLowerCase();
    const idx = displayedPredictions.findIndex((pick) => {
      const name = (pick.playerName ?? pick.player_name ?? '').toString().toLowerCase();
      return name === needle || name.includes(needle);
    });

    if (idx >= 0) {
      setExpandedRows([idx]);
      expandAppliedRef.current = true;
      expandMissCountRef.current = 0;
      return;
    }

    expandMissCountRef.current += 1;
    if (expandMissCountRef.current >= MAX_EXPAND_MISS_ATTEMPTS) {
      expandAppliedRef.current = true;
    }
  }, [loading, displayedPredictions, searchParams, setExpandedRows]);
}
